import {
  ConversationFilterNames,
  conversationFilterData,
} from "@redotech/effect-api-shared/schema/conversation-filters";
import { useHandler } from "@redotech/react-util/hook";
import FilterLinesIcon from "@redotech/redo-icons/filter-lines_filled.svg";
import XCloseIcon from "@redotech/redo-icons/x_filled.svg";
import type { ArrayStringFilterData } from "@redotech/redo-model/views/advanced-filters/array-string-filter";
import type { DateFilterData } from "@redotech/redo-model/views/advanced-filters/date-filter";
import type { ExistsFilterData } from "@redotech/redo-model/views/advanced-filters/exists-filter";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoListDropdown } from "@redotech/redo-web/arbiter-components/list/redo-list-dropdown";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { deepCopyObject } from "@redotech/util/object";
import { assertNever } from "@redotech/util/type";
import { memo, useMemo, useState } from "react";
import { useDebounce } from "usehooks-ts";
import {
  useAddFilter,
  useAdvancedFilters,
  useStatusFilter,
} from "./filters-context";

type StatusValue = "OPEN" | "CLOSED" | "IN_PROGRESS" | "SNOOZED" | "all";

const STATUS_OPTIONS: RedoListItem<StatusValue>[] = [
  { id: "OPEN", value: "OPEN", type: "text", text: "Open" },
  { id: "CLOSED", value: "CLOSED", type: "text", text: "Closed" },
  {
    id: "IN_PROGRESS",
    value: "IN_PROGRESS",
    type: "text",
    text: "In Progress",
  },
  { id: "SNOOZED", value: "SNOOZED", type: "text", text: "Snoozed" },
  { id: "all", value: "all", type: "text", text: "All" },
];

type FilterFieldKey =
  | "ASSIGNEES"
  | "CHANNELS"
  | "CONVERSATION_TAGS"
  | "CLOSED_DATE"
  | "MENTIONS"
  | "CREATED_DATE"
  | "CUSTOMER_TAGS"
  | "LAST_RESPONSE_AT"
  | "READ"
  | "WORDS";

const FILTER_OPTIONS: RedoListItem<FilterFieldKey>[] = [
  { id: "ASSIGNEES", value: "ASSIGNEES", type: "text", text: "Assignees" },
  { id: "CHANNELS", value: "CHANNELS", type: "text", text: "Channels" },
  { id: "READ", value: "READ", type: "text", text: "Read Status" },
  {
    id: "CONVERSATION_TAGS",
    value: "CONVERSATION_TAGS",
    type: "text",
    text: "Conversation Tags",
  },
  { id: "WORDS", value: "WORDS", type: "text", text: "Keywords" },
  {
    id: "CUSTOMER_TAGS",
    value: "CUSTOMER_TAGS",
    type: "text",
    text: "Customer Tags",
  },
  {
    id: "CREATED_DATE",
    value: "CREATED_DATE",
    type: "text",
    text: "Created Date",
  },
  {
    id: "LAST_RESPONSE_AT",
    value: "LAST_RESPONSE_AT",
    type: "text",
    text: "Last Response",
  },
  {
    id: "CLOSED_DATE",
    value: "CLOSED_DATE",
    type: "text",
    text: "Closed Date",
  },
  { id: "MENTIONS", value: "MENTIONS", type: "text", text: "Mentions" },
];

export const ConversationsFilterToolbar = memo(
  function ConversationsFilterToolbar() {
    const { status, setStatus } = useStatusFilter();
    const { filters, setFilters } = useAdvancedFilters();
    const addFilter = useAddFilter();

    const wordsFilter = useMemo(() => {
      return filters.find(
        (f): f is ArrayStringFilterData =>
          f.name === ConversationFilterNames.WORDS,
      );
    }, [filters]);

    const wordsValue = useMemo(() => {
      if (!wordsFilter?.value || wordsFilter.value.length === 0) {
        return "";
      }
      return wordsFilter.value[0] ?? "";
    }, [wordsFilter]);

    const [searchString, setSearchString] = useState(wordsValue);
    const debouncedSearch = useDebounce(searchString, 500);

    const [filtersDropdownOpen, setFiltersDropdownOpen] = useState(false);
    const [filtersDropdownButtonRef, setFiltersDropdownButtonRef] =
      useState<HTMLButtonElement | null>(null);

    const handleSearchChange = useHandler((value: string) => {
      setSearchString(value);
    });

    useMemo(() => {
      const currentSearch = wordsValue;
      if (debouncedSearch !== currentSearch) {
        setFilters((prev) => {
          const otherFilters = prev.filter(
            (f) => f.name !== ConversationFilterNames.WORDS,
          );
          if (debouncedSearch) {
            const newWordsFilter: ArrayStringFilterData = deepCopyObject(
              conversationFilterData[
                ConversationFilterNames.WORDS
              ] as ArrayStringFilterData,
            );
            newWordsFilter.value = [debouncedSearch];
            return [...otherFilters, newWordsFilter];
          }
          return otherFilters;
        });
      }
    }, [debouncedSearch, wordsValue, setFilters]);

    const handleClearSearch = useHandler(() => {
      setSearchString("");
      setFilters((prev) =>
        prev.filter((f) => f.name !== ConversationFilterNames.WORDS),
      );
    });

    const handleStatusChange = useHandler(
      ({ item }: { item: RedoListItem<StatusValue> }) => {
        setStatus(item.value === "all" ? null : item.value);
      },
    );

    const handleAddFilter = useHandler(
      ({ item }: { item: RedoListItem<FilterFieldKey> }) => {
        const filterKey = item.value;

        switch (filterKey) {
          case "ASSIGNEES": {
            const filter: ArrayStringFilterData = deepCopyObject(
              conversationFilterData[
                ConversationFilterNames.ASSIGNEES
              ] as ArrayStringFilterData,
            );
            addFilter(filter);
            break;
          }
          case "CHANNELS": {
            const filter: ArrayStringFilterData = deepCopyObject(
              conversationFilterData[
                ConversationFilterNames.CHANNELS
              ] as ArrayStringFilterData,
            );
            addFilter(filter);
            break;
          }
          case "READ": {
            const filter: ExistsFilterData = deepCopyObject(
              conversationFilterData[
                ConversationFilterNames.READ_AT
              ] as ExistsFilterData,
            );
            addFilter(filter);
            break;
          }
          case "CONVERSATION_TAGS": {
            const filter: ArrayStringFilterData = deepCopyObject(
              conversationFilterData[
                ConversationFilterNames.CONVERSATION_TAGS
              ] as ArrayStringFilterData,
            );
            addFilter(filter);
            break;
          }
          case "WORDS": {
            const filter: ArrayStringFilterData = deepCopyObject(
              conversationFilterData[
                ConversationFilterNames.WORDS
              ] as ArrayStringFilterData,
            );
            addFilter(filter);
            break;
          }
          case "CUSTOMER_TAGS": {
            const filter: ArrayStringFilterData = deepCopyObject(
              conversationFilterData[
                ConversationFilterNames.CUSTOMER_TAGS
              ] as ArrayStringFilterData,
            );
            addFilter(filter);
            break;
          }
          case "CREATED_DATE": {
            const filter: DateFilterData = deepCopyObject(
              conversationFilterData[
                ConversationFilterNames.CREATED_AT
              ] as DateFilterData,
            );
            addFilter(filter);
            break;
          }
          case "LAST_RESPONSE_AT": {
            const filter: DateFilterData = deepCopyObject(
              conversationFilterData[
                ConversationFilterNames.LAST_RESPONSE_AT
              ] as DateFilterData,
            );
            addFilter(filter);
            break;
          }
          case "CLOSED_DATE": {
            const filter: DateFilterData = deepCopyObject(
              conversationFilterData[
                ConversationFilterNames.CLOSED_AT
              ] as DateFilterData,
            );
            addFilter(filter);
            break;
          }
          case "MENTIONS": {
            const filter: ArrayStringFilterData = deepCopyObject(
              conversationFilterData[
                ConversationFilterNames.MENTIONS
              ] as ArrayStringFilterData,
            );
            addFilter(filter);
            break;
          }
          default:
            assertNever(filterKey);
        }

        setFiltersDropdownOpen(false);
      },
    );

    const selectedStatusItem = useMemo(() => {
      if (!status) {
        return STATUS_OPTIONS.find((o) => o.id === "all");
      }
      return STATUS_OPTIONS.find((o) => o.id === status);
    }, [status]);

    return (
      <>
        <RedoListDropdown
          dropdownAnchor={filtersDropdownButtonRef}
          dropdownOpen={filtersDropdownOpen}
          fitToAnchor={false}
          items={FILTER_OPTIONS}
          itemSelected={handleAddFilter}
          setDropdownOpen={setFiltersDropdownOpen}
          size="xs"
        />
        <Flex align="center" gap="md">
          <RedoTextInput
            placeholder="Search"
            setValue={handleSearchChange}
            trailingActions={
              searchString ? (
                <Flex onClick={handleClearSearch} style={{ cursor: "pointer" }}>
                  <XCloseIcon />
                </Flex>
              ) : undefined
            }
            value={searchString}
          />
          <RedoButton
            hierarchy="secondary"
            IconLeading={FilterLinesIcon}
            onClick={() => setFiltersDropdownOpen((prev) => !prev)}
            ref={setFiltersDropdownButtonRef}
          />
          <RedoSingleSelectDropdownInput
            fitToAnchor={false}
            options={STATUS_OPTIONS}
            optionSelected={handleStatusChange}
            selectedItem={selectedStatusItem}
            size="xs"
          />
        </Flex>
      </>
    );
  },
);
